<template>
    <div class="custom-table-items">
        <div v-if="loading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Yükleniyor...</span>
            </div>
        </div>

        <table class="table">
            <thead class="thead-light">
                <tr>
                    <th>{{ $t('views.transactions.columns.date') }}</th>
                    <th>{{ $t('views.transactions.columns.description') }}</th>
                    <th>{{ $t('views.transactions.columns.category') }}</th>
                    <th>{{ $t('views.transactions.columns.amount') }}</th>
                    <th>{{ $t('views.transactions.columns.actions') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="isAddingNew">
                    <td style="position: relative">
                        <div class="date-picker-wrapper" style="max-width: 150px">
                            <VueDatePicker
                                v-model="newItem.date"
                                locale="tr"
                                auto-apply
                                :model-value="newItem.date"
                                format="dd.MM.yyyy"
                                :action-row="{ showPreview: false }"
                                :enable-time-picker="false"
                                :clearable="false"
                                :ui-locale="$i18n.locale"
                                key="new-date-picker"
                            >
                                <template #input-icon>
                                    <i class="bi bi-calendar-week dp__input_icon dp__input_icons" style="margin-top: -4px"></i>
                                </template>
                            </VueDatePicker>
                        </div>
                    </td>
                    <td>
                        <input v-model="newItem.description" type="text" class="form-control form-control-sm" placeholder="Sebep giriniz..." />
                    </td>
                    <td>
                        <div class="dropdown" ref="newCategoryDropdown">
                            <input v-model="newCategorySearch" @focus="showNewCategoryOptions = true" @input="onNewCategorySearch" type="text" class="form-control form-control-sm" placeholder="Kategori arayınız..." />
                            <div v-if="showNewCategoryOptions" class="dropdown-menu show">
                                <div v-if="categoriesLoading" class="dropdown-item text-center">
                                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                                        <span class="visually-hidden">Yükleniyor...</span>
                                    </div>
                                </div>
                                <a v-else-if="filteredNewCategories.length === 0" class="dropdown-item text-muted" href="#"> Sonuç bulunamadı </a>
                                <a v-else v-for="category in filteredNewCategories" :key="category" @click="selectNewCategory(category)" class="dropdown-item" href="#">
                                    {{ category }}
                                </a>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div class="input-group input-group-sm">
                            <select v-model="newItem.currency" class="form-select form-select-sm currency-select">
                                <option v-for="currency in availableCurrencies" :key="currency" :value="currency">{{ currency }}</option>
                            </select>
                            <input v-model.number="newItem.amount" type="number" step="0.01" min="0" class="form-control form-control-sm" />
                        </div>
                    </td>
                    <td>
                        <div class="d-flex" style="gap: 5px">
                            <button class="btn btn-sm btn-success" @click="saveNewItem()" :disabled="saveLoading">
                                <div v-if="saveLoading" class="spinner-border spinner-border-sm" role="status"></div>
                                <i v-else class="fa fa-check"></i>
                            </button>
                            <button class="btn btn-sm btn-danger" @click="cancelAddNew()" :disabled="saveLoading">
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                    </td>
                </tr>
                <tr v-if="!loading && error">
                    <td colspan="5" class="text-center py-4 text-danger">
                        {{ error }}
                    </td>
                </tr>
                <tr v-else-if="!loading && transactions.length === 0">
                    <td colspan="5" class="text-center py-4">
                        {{ $t('views.transactions.noData') }}
                    </td>
                </tr>
                <tr v-for="(item, index) in transactions" :key="index" :class="{ 'amazon-row': item.category === 'Amazon' }">
                    <td>
                        <div v-if="editingId !== item.id">{{ formatDate(item.transaction_date) }}</div>
                        <div v-else class="date-picker-wrapper" style="max-width: 150px; position: relative">
                            <VueDatePicker
                                v-model="editingItem.transaction_date"
                                :model-value="editingItem.transaction_date"
                                locale="tr"
                                auto-apply
                                format="dd.MM.yyyy"
                                :action-row="{ showPreview: false }"
                                :enable-time-picker="false"
                                :clearable="false"
                                :ui-locale="$i18n.locale"
                                key="edit-date-picker"
                            >
                                <template #input-icon>
                                    <i class="bi bi-calendar-week dp__input_icon dp__input_icons" style="margin-top: -4px"></i>
                                </template>
                            </VueDatePicker>
                        </div>
                    </td>
                    <td>
                        <div v-if="editingId !== item.id">
                            <a v-if="item.hasDetail === 1" class="text-primary text-decoration-underline" href="javascript:;" @click="viewDetails(item.id)">{{ item.reason || item.reason_detail }}</a>
                            <span v-else>{{ item.reason || item.reason_detail }}</span>
                        </div>
                        <input v-else v-model="editingItem.reason" type="text" class="form-control form-control-sm" />
                    </td>
                    <td>
                        <div v-if="editingId !== item.id">{{ item.category }}</div>
                        <div v-else class="dropdown" ref="categoryDropdown">
                            <input v-model="categorySearch" @focus="showCategoryOptions = true" @input="onCategorySearch" type="text" class="form-control form-control-sm" placeholder="Kategori arayınız..." />
                            <div v-if="showCategoryOptions" class="dropdown-menu show">
                                <div v-if="categoriesLoading" class="dropdown-item text-center">
                                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                                        <span class="visually-hidden">Yükleniyor...</span>
                                    </div>
                                </div>
                                <a v-else-if="filteredCategories.length === 0" class="dropdown-item text-muted" href="#"> Sonuç bulunamadı </a>
                                <a v-else v-for="category in filteredCategories" :key="category" @click="selectCategory(category)" class="dropdown-item" href="#">
                                    {{ category }}
                                </a>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div v-if="editingId !== item.id">
                            {{ item.currency }} <strong class="text-secondary">+{{ formatNumber(item.amount) }} </strong>
                            <Popper v-if="item.currency_original && item.currency_original !== item.currency" class="light-popper" hover placement="bottom" arrow style="margin: 0; border: 0">
                                <i class="bi bi-arrow-left-right fs-6 text-muted convert-icon"></i>
                                <template #content>
                                    <span class="fw-normal">
                                        {{ item.currency_original }} <strong> +{{ formatNumber(Math.abs(item.amount_original)) }}</strong>
                                    </span>
                                </template>
                            </Popper>
                        </div>
                        <div v-else>
                            <div class="input-group input-group-sm">
                                <select v-model="editingItem.currency" class="form-select form-select-sm currency-select">
                                    <option v-for="currency in availableCurrencies" :key="currency" :value="currency">{{ currency }}</option>
                                </select>
                                <input v-model.number="editingItem.amount" type="number" step="0.01" min="0" class="form-control form-control-sm" />
                            </div>
                        </div>
                    </td>
                    <td>
                        <div v-if="editingId !== item.id">
                            <button
                                class="dropdown btn btn-sm btn-outline-secondary dropdown-toggle"
                                data-bs-toggle="dropdown"
                                :disabled="item.category === 'Amazon'"
                                :title="item.category === 'Amazon' ? $t('views.transactions.errors.amazonCantBeEdited') : ''"
                            >
                                <i class="bi bi-three-dots-vertical"></i>
                            </button>
                            <div class="dropdown-menu dropdown-menu-end navbar-dropdown-menu navbar-dropdown-menu-borderless navbar-dropdown-account" aria-labelledby="accountNavbarDropdown">
                                <a v-if="item.category !== 'Amazon'" class="dropdown-item" href="javascript:;" @click="startEdit(item)"> <i class="fa fa-edit me-2"></i> {{ $t('views.transactions.actions.edit') }} </a>
                                <a v-if="item.category !== 'Amazon'" class="dropdown-item" href="javascript:;" @click="deleteItem(item)"> <i class="fa fa-trash me-2"></i> {{ $t('views.transactions.actions.delete') }} </a>
                                <a v-if="item.category === 'Amazon'" class="dropdown-item text-muted" href="javascript:;"> <i class="fa fa-lock me-2"></i> {{ $t('views.transactions.actions.locked') }} </a>
                            </div>
                        </div>

                        <div v-else class="d-flex" style="gap: 5px">
                            <button class="btn btn-sm btn-success" @click="saveEdit()" :disabled="editLoading">
                                <div v-if="editLoading" class="spinner-border spinner-border-sm" role="status"></div>
                                <i v-else class="fa fa-check"></i>
                            </button>
                            <button class="btn btn-sm btn-danger" @click="cancelEdit()" :disabled="editLoading">
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
// import DateRangePicker from '@/components/DateRangePicker.vue'
import { countryInfo } from '@/utils/helper'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import Popper from 'vue3-popper'
import { mapActions, mapGetters, mapState } from 'vuex'

// Bildirimler için basit bir fonksiyon - toast bileşeni yoksa artık kullanılmıyor
function showNotification(message, type = 'error') {
    if (type === 'error') {
        alert(message)
    } else {
        alert(message)
    }
}

export default {
    name: 'TransactionsIncomes',
    components: {
        // DateRangePicker,
        VueDatePicker,
        Popper,
    },
    data() {
        return {
            editingId: null,
            editingItem: {},
            categorySearch: '',
            showCategoryOptions: false,
            filteredCategories: [],
            isAddingNew: false,
            newItem: {
                date: new Date(),
                description: '',
                category: '',
                amount: 0,
                currency: '',
            },
            // Desteklenen para birimleri
            availableCurrencies: ['USD', 'GBP', 'EUR', 'CAD', 'AUD', 'TRY'],
            newCategorySearch: '',
            showNewCategoryOptions: false,
            filteredNewCategories: [],
            // Date picker için
            editPickerDate: new Date(),
            // Debounce için timeout ID'leri
            searchTimeout: null,
            newSearchTimeout: null,
            // Loading state'leri
            saveLoading: false,
            editLoading: false,
        }
    },
    computed: {
        ...mapState('transactions', {
            incomeData: (state) => state.incomes,
            filterParams: (state) => state.filterParams,
        }),
        ...mapGetters('transactions', ['getFormattedTransactions', 'getCategoryList', 'getCategoriesLoading']),
        ...mapGetters('market', ['currentMarket']),

        transactions() {
            return this.getFormattedTransactions('income')
        },
        loading() {
            return this.incomeData.loading
        },
        error() {
            return this.incomeData.error
        },
        categories() {
            return this.getCategoryList
        },
        categoriesLoading() {
            return this.getCategoriesLoading
        },
        currencySymbol() {
            const countries = countryInfo()

            if (this.currentMarket && this.currentMarket.short) {
                return countries[this.currentMarket.short].symbol || countries[this.currentMarket.short].currency_text || 'USD'
            }

            return 'USD'
        },
    },
    created() {
        document.addEventListener('click', this.handleOutsideClick)

        this.setDefaultCurrency()
    },
    beforeUnmount() {
        // Component yok edilmeden önce event listener'ları kaldırıyoruz
        document.removeEventListener('click', this.handleOutsideClick)

        // Timeoutları temizle
        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        if (this.newSearchTimeout) clearTimeout(this.newSearchTimeout)
    },
    methods: {
        ...mapActions('transactions', ['getTransactions', 'createTransaction', 'updateTransaction', 'deleteTransaction', 'getCategories']),

        loadIncomes() {
            console.log('Income verilerini yenileme isteği alındı')
        },
        formatNumber(number) {
            return Number(number).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        formatDate(dateString) {
            // ISO tarih formatından dd.mm.yyyy formatına çevirme
            if (!dateString) return ''

            const date = new Date(dateString)
            const day = date.getDate().toString().padStart(2, '0')
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const year = date.getFullYear()

            return `${day}.${month}.${year}`
        },
        viewDetails(id) {
            this.$router.push({
                name: 'transactions-details',
                params: {
                    id,
                    type: 'incomes',
                },
            })
        },
        async startEdit(item) {
            if (item.category === 'Amazon') {
                // Eğer this.$toast kullanılabilirse, onu kullanıyoruz, değilse showNotification fonksiyonunu
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.errors.amazonCantBeEdited'))
                } else {
                    showNotification(this.$t('views.transactions.errors.amazonCantBeEdited'))
                }
                return
            }

            this.editingId = item.id
            this.editingItem = JSON.parse(JSON.stringify(item)) // Derin kopya oluştur
            this.categorySearch = item.category

            // Orijinal para birimi ve tutarı kullan
            if (this.editingItem.currency_original) {
                this.editingItem.currency = this.editingItem.currency_original
            }

            if (this.editingItem.amount_original !== undefined) {
                this.editingItem.amount = Math.abs(this.editingItem.amount_original)
            }

            // remove currency_original and amount_original
            delete this.editingItem.currency_original
            delete this.editingItem.amount_original

            // Eğer düzenlenen öğenin para birimi desteklenen para birimleri listesinde yoksa varsayılan para birimine ayarla
            if (!this.availableCurrencies.includes(this.editingItem.currency)) {
                if (this.currentMarket && this.currentMarket.short) {
                    const countries = countryInfo()
                    this.editingItem.currency = countries[this.currentMarket.short].currency_text
                } else {
                    this.editingItem.currency = 'USD'
                }
            }

            // Tarih seçici için tarihi ayarla - geçerli bir ISO tarih formatı olmasını sağla
            try {
                if (this.editingItem.transaction_date) {
                    let date
                    if (typeof this.editingItem.transaction_date === 'string') {
                        // YYYY-MM-DD formatına uygun tarih düzenleme
                        if (!this.editingItem.transaction_date.includes('T')) {
                            const [year, month, day] = this.editingItem.transaction_date.split('-')
                            date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
                        } else {
                            date = new Date(this.editingItem.transaction_date)
                        }
                    } else {
                        date = new Date(this.editingItem.transaction_date)
                    }

                    if (isNaN(date.getTime())) {
                        throw new Error('Geçersiz tarih')
                    }

                    this.editPickerDate = date
                } else {
                    this.editPickerDate = new Date()
                }
            } catch (e) {
                console.error('Tarih dönüşüm hatası:', e)
                this.editPickerDate = new Date()
            }

            // Kategori listesini baştan yükleme - bunun yerine mevcut kategorileri filtreleme
            this.filteredCategories = [...this.categories]
        },
        async saveEdit() {
            try {
                this.editLoading = true
                this.editingItem.category = this.categorySearch

                if (this.editingItem.transaction_date) {
                    if (this.editingItem.transaction_date instanceof Date) {
                        this.editingItem.transaction_date = this.editingItem.transaction_date.toISOString().split('T')[0]
                    } else if (typeof this.editingItem.transaction_date === 'string' && this.editingItem.transaction_date.includes('T')) {
                        this.editingItem.transaction_date = this.editingItem.transaction_date.split('T')[0]
                    }
                }

                const transaction = {
                    ...this.editingItem,
                    type: 'income',
                    transaction_type: 'USER',
                    currency: this.editingItem.currency,
                    amount: this.editingItem.amount,
                }

                const itemId = this.editingId

                this.cancelEdit()

                setTimeout(async () => {
                    try {
                        await this.updateTransaction({
                            id: itemId,
                            transaction,
                        })

                        // Başarılı işlem sonrası verileri yeniden çekmek için ana bileşenin getTransactions metodunu çağır
                        this.$parent.getTransactions('income')
                    } catch (error) {
                        console.error('Güncelleme hatası (gecikmeli):', error)
                    }
                }, 100)
            } catch (error) {
                console.error('Güncelleme hatası:', error)
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.errors.updateFailed'))
                } else {
                    showNotification(this.$t('views.transactions.errors.updateFailed'))
                }
            } finally {
                this.editLoading = false
            }
        },
        cancelEdit() {
            // Diğer açık kontrolleri kapat
            document.removeEventListener('click', this.handleOutsideClick)

            // State'i temizle
            this.editingId = null
            this.editingItem = {}
            this.categorySearch = ''
            this.showCategoryOptions = false

            // Timeout içindeki işlemleri iptal et
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }
        },
        async deleteItem(item) {
            if (item.category === 'Amazon') {
                // Eğer this.$toast kullanılabilirse, onu kullanıyoruz, değilse showNotification fonksiyonunu
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.errors.amazonCantBeDeleted'))
                } else {
                    showNotification(this.$t('views.transactions.errors.amazonCantBeDeleted'))
                }
                return
            }

            if (confirm(this.$t('views.transactions.confirmDelete'))) {
                try {
                    await this.deleteTransaction({ id: item.id, type: 'income' })

                    // Başarılı silme işlemi sonrası ana bileşene bildirim yapıyoruz
                    this.$parent.getTransactions('income')
                } catch (error) {
                    console.error('Silme hatası:', error)
                }
            }
        },
        onCategorySearch() {
            // Debounce uygula
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }

            this.searchTimeout = setTimeout(() => {
                // API çağrısı yapmak yerine client tarafında filtreleme yap
                if (this.categorySearch) {
                    this.filteredCategories = this.categories.filter((category) => category.toLowerCase().includes(this.categorySearch.toLowerCase())).slice(0, 10)
                } else {
                    this.filteredCategories = [...this.categories].slice(0, 10)
                }
            }, 300) // 300ms bekle
        },
        async loadAllCategories() {
            try {
                // Kategorileri yükle - sadece bir kez API'ye istek at
                await this.getCategories({ type: 'income', search: '', all: true })

                // İlk 10 öğeyi göster
                this.filteredCategories = [...this.categories].slice(0, 10)
                this.filteredNewCategories = [...this.categories].slice(0, 10)
            } catch (error) {
                console.error('Kategori yükleme hatası:', error)
                if (this.$toast) {
                    this.$toast.error('Kategoriler yüklenirken bir hata oluştu')
                }
            }
        },
        async fetchCategories(type, search) {
            // Bu metot artık sadece özel durumlar için kullanılacak
            // Örneğin yeni kategori ekleme işlemlerinden sonra yeniden yükleme
            try {
                await this.getCategories({ type, search, all: true })
                // Tüm kategorileri al
                let allCategories = [...this.categories]
                // İlk 10 öğeyi göster
                this.filteredCategories = allCategories.slice(0, 10)
                this.filteredNewCategories = allCategories.slice(0, 10)

                return allCategories
            } catch (error) {
                console.error('Kategori yükleme hatası:', error)
                return []
            }
        },
        selectCategory(category) {
            this.categorySearch = category
            this.showCategoryOptions = false
        },
        startAddNew() {
            // Component'i düzenleme moduna al
            this.isAddingNew = true
            // Yeni item'ı oluştur
            this.newItem = {
                date: new Date(),
                description: '',
                category: '',
                amount: 0,
                currency: '',
            }
            // Varsayılan para birimini ayarla
            this.setDefaultCurrency()

            this.newCategorySearch = ''
            // Kategori listesini baştan yükleme yerine mevcut kategorileri kullanma
            this.filteredNewCategories = [...this.categories]
        },
        async saveNewItem() {
            // Zorunlu alanları kontrol et
            if (!this.newItem.description) {
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.validation.descriptionRequired'))
                } else {
                    showNotification(this.$t('views.transactions.validation.descriptionRequired'))
                }
                return
            }

            if (!this.newCategorySearch) {
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.validation.categoryRequired'))
                } else {
                    showNotification(this.$t('views.transactions.validation.categoryRequired'))
                }
                return
            }

            if (!this.newItem.amount) {
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.validation.amountRequired'))
                } else {
                    showNotification(this.$t('views.transactions.validation.amountRequired'))
                }
                return
            }

            try {
                this.saveLoading = true

                // ISO formatında tarihi al (YYYY-MM-DD)
                let dateStr = ''
                if (this.newItem.date) {
                    try {
                        const date = new Date(this.newItem.date)
                        dateStr = date.toISOString().split('T')[0]
                    } catch (e) {
                        console.error('Tarih dönüşüm hatası:', e)
                        dateStr = new Date().toISOString().split('T')[0]
                    }
                } else {
                    dateStr = new Date().toISOString().split('T')[0]
                }

                // Yeni veriyi oluştur
                const newTransaction = {
                    transaction_date: dateStr,
                    reason: this.newItem.description,
                    category: this.newCategorySearch,
                    amount: this.newItem.amount,
                    currency: this.newItem.currency || (this.currentMarket ? countryInfo()[this.currentMarket.short].currency_text : 'USD'),
                    type: 'income',
                    transaction_type: 'USER',
                }

                // Önce formu kapat, sonra store çağrısını yap
                this.cancelAddNew()

                // Timeout kullanarak DOM güncellemesinin tamamlanmasını bekleyelim
                setTimeout(async () => {
                    try {
                        // Store aracılığıyla yeni kayıt oluştur
                        const result = await this.createTransaction(newTransaction)

                        if (!result || !result.success) {
                            // İşlem başarısız olduysa bildirim göster
                            if (this.$toast) {
                                this.$toast.error(this.$t('views.transactions.errors.createFailed'))
                            } else {
                                showNotification(this.$t('views.transactions.errors.createFailed'))
                            }
                            return
                        }

                        // Başarılı işlem sonrası verileri yeniden çekmek için ana bileşenin getTransactions metodunu çağır
                        this.$parent.getTransactions('income')
                    } catch (error) {
                        console.error('Ekleme hatası (gecikmeli):', error)
                        if (this.$toast) {
                            this.$toast.error(this.$t('views.transactions.errors.createFailed'))
                        } else {
                            showNotification(this.$t('views.transactions.errors.createFailed'))
                        }
                    }
                }, 100)
            } catch (error) {
                console.error('Ekleme hatası:', error)
                if (this.$toast) {
                    this.$toast.error(this.$t('views.transactions.errors.createFailed'))
                } else {
                    showNotification(this.$t('views.transactions.errors.createFailed'))
                }
            } finally {
                this.saveLoading = false
            }
        },
        cancelAddNew() {
            this.isAddingNew = false
            this.newItem = {
                date: new Date(),
                description: '',
                category: '',
                amount: 0,
                currency: '',
            }
            this.newCategorySearch = ''
            this.showNewCategoryOptions = false
        },
        onNewCategorySearch() {
            // Debounce uygula
            if (this.newSearchTimeout) {
                clearTimeout(this.newSearchTimeout)
            }

            this.newSearchTimeout = setTimeout(() => {
                // API çağrısı yapmak yerine client tarafında filtreleme yap
                if (this.newCategorySearch) {
                    this.filteredNewCategories = this.categories.filter((category) => category.toLowerCase().includes(this.newCategorySearch.toLowerCase())).slice(0, 10)
                } else {
                    this.filteredNewCategories = [...this.categories].slice(0, 10)
                }
            }, 300) // 300ms bekle
        },
        selectNewCategory(category) {
            this.newCategorySearch = category
            this.showNewCategoryOptions = false
        },
        handleOutsideClick(event) {
            // Eğer tıklama dropdown içinde değilse dropdown'ı kapat
            if (this.$refs.categoryDropdown) {
                // Dizi olabilir veya tek bir element
                const dropdowns = Array.isArray(this.$refs.categoryDropdown) ? this.$refs.categoryDropdown : [this.$refs.categoryDropdown]

                // Tıklama herhangi bir dropdown içinde mi kontrol ediyorum
                const isClickInside = dropdowns.some((dropdown) => dropdown && dropdown.contains && dropdown.contains(event.target))

                if (!isClickInside) {
                    this.showCategoryOptions = false
                }
            }

            // Yeni kategori dropdown kontrolü
            if (this.$refs.newCategoryDropdown) {
                // Dizi olabilir veya tek bir element
                const dropdowns = Array.isArray(this.$refs.newCategoryDropdown) ? this.$refs.newCategoryDropdown : [this.$refs.newCategoryDropdown]

                // Tıklama herhangi bir dropdown içinde mi kontrol ediyorum
                const isClickInside = dropdowns.some((dropdown) => dropdown && dropdown.contains && dropdown.contains(event.target))

                if (!isClickInside) {
                    this.showNewCategoryOptions = false
                }
            }
        },
        setDefaultCurrency() {
            // Varsayılan para birimini mevcut pazardan al
            if (this.currentMarket && this.currentMarket.short) {
                const countries = countryInfo()
                const defaultCurrency = countries[this.currentMarket.short].currency_text
                this.newItem.currency = defaultCurrency
            } else {
                this.newItem.currency = 'USD'
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.custom-table-items {
    max-width: 100% !important;
    overflow: visible;
    min-height: 400px !important;
    position: relative;

    .spinner-border {
        width: 3rem;
        height: 3rem;
    }

    table {
        thead {
            top: 0;
            position: sticky;
            z-index: 10;
        }
    }

    tr {
        border-bottom: 1px solid #e7eaf3b2;
    }

    td {
        vertical-align: middle;
        height: 50px;
        padding-top: 8px;
        padding-bottom: 8px;
        color: #677788 !important;
        font-size: 0.8rem;
        font-weight: 400;

        strong {
            color: #00203f;
            font-weight: 600;
        }
    }
}

.thead-light {
    th {
        background-color: #f8fafd !important;
        border-bottom: 1px solid #e7eaf3b2 !important;
        color: #677788 !important;
        font-size: 11.9px !important;
        font-weight: 400 !important;
        line-height: 17.85px;
        font-weight: bold !important;
    }
}

.no-caret::after {
    display: none !important;
}

.dropdown-toggle::after {
    display: none !important;
}

.date-picker {
    width: 100%;
    font-size: 0.875rem;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    border: 1px solid #e7eaf3;

    :deep(.dp__input) {
        padding: 0;
        font-size: 0.875rem;
        height: 31px;
    }
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
}

.date-input {
    cursor: pointer;
    background-color: #fff;
}

.date-range-picker {
    position: absolute;
    z-index: 100;
}

.date-picker-wrapper {
    position: relative;
    width: 100%;
}

.date-picker-container {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    z-index: 1050;
}

.datepicker-mount-point {
    min-width: 270px;
}

.currency-select {
    width: 80px !important;
    min-width: 80px !important;
    flex: 0 0 80px !important;
    padding-right: 0px !important;
}

.convert-icon {
    cursor: pointer;
    margin-left: 3px;
    opacity: 0.8;
    color: #ffb53e !important;
    &:hover {
        opacity: 1;
    }
}

.light-popper {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
}
</style>
